import React, { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import { Calendar, AlertCircle, RefreshCw } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { useLanguage } from '../context/LanguageContext';
import { getUpcomingSubscriptions } from '../lib/api';
import { formatCurrency } from '../lib/utils';

/**
 * Widget displaying upcoming subscription payments with window filter.
 * Filters: this_month | 30d (default) | 90d
 */
export default function UpcomingPaymentsWidget() {
  const { t, language } = useLanguage();
  const [window, setWindow] = useState('30d');
  const [data, setData] = useState({ upcoming: [], total: 0, count: 0 });
  const [isLoading, setIsLoading] = useState(true);

  const load = useCallback(async () => {
    setIsLoading(true);
    try {
      const res = await getUpcomingSubscriptions(window);
      setData(res.data || { upcoming: [], total: 0, count: 0 });
    } catch (e) {
      console.error('Failed to load upcoming subscriptions', e);
    } finally {
      setIsLoading(false);
    }
  }, [window]);

  useEffect(() => { load(); }, [load]);

  const locale = language === 'fr' ? 'fr-FR' : language === 'pt' ? 'pt-PT' : 'en-US';
  const fmtDate = (iso) => {
    if (!iso) return '';
    try {
      return new Date(iso + 'T00:00:00').toLocaleDateString(locale, { day: '2-digit', month: 'short', year: 'numeric' });
    } catch { return iso; }
  };

  const filters = [
    { key: 'this_month', label: t('filter_this_month') },
    { key: '30d', label: t('filter_30_days') },
    { key: '90d', label: t('filter_90_days') },
  ];

  return (
    <Card className="mb-6" data-testid="upcoming-payments-widget">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <CardTitle className="text-lg flex items-center gap-2">
            <Calendar className="w-5 h-5 text-primary" />
            {t('upcoming_payments')}
          </CardTitle>
          <div className="flex gap-1 flex-wrap">
            {filters.map(f => (
              <Button
                key={f.key}
                variant={window === f.key ? 'default' : 'outline'}
                size="sm"
                className="h-7 text-xs"
                onClick={() => setWindow(f.key)}
                data-testid={`upcoming-filter-${f.key}`}
              >
                {f.label}
              </Button>
            ))}
          </div>
        </div>
      </CardHeader>
      <CardContent className="pt-0">
        {isLoading ? (
          <div className="flex justify-center py-8">
            <RefreshCw className="w-5 h-5 animate-spin text-muted-foreground" />
          </div>
        ) : data.upcoming.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground" data-testid="upcoming-empty">
            <AlertCircle className="w-10 h-10 mx-auto mb-2 opacity-50" />
            <p className="text-sm">{t('no_upcoming_payments')}</p>
          </div>
        ) : (
          <>
            <div className="flex items-center justify-between mb-3 p-3 rounded-lg bg-primary/5 border border-primary/20">
              <span className="text-sm font-medium">
                {t('total_to_plan')} ({data.count})
              </span>
              <span className="text-lg font-bold text-primary" data-testid="upcoming-total">
                {formatCurrency(data.total)}
              </span>
            </div>
            <div className="space-y-2">
              {data.upcoming.map((sub, idx) => (
                <motion.div
                  key={sub.subscription_id}
                  initial={{ opacity: 0, y: 5 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: idx * 0.03 }}
                  className="flex items-center justify-between p-2 rounded-md hover:bg-muted/50"
                  data-testid={`upcoming-item-${sub.subscription_id}`}
                >
                  <div className="flex-1 min-w-0">
                    <p className="font-medium truncate">{sub.name}</p>
                    <p className="text-xs text-muted-foreground">
                      {fmtDate(sub.next_payment_date)}
                    </p>
                  </div>
                  <p className="font-semibold text-sm whitespace-nowrap ml-2">
                    {formatCurrency(sub.amount)}
                  </p>
                </motion.div>
              ))}
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}
